import { useCallback, useEffect, useRef, useState } from 'react';
import { ParticipantTile, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';

import { ParticipantNameplate } from './ParticipantNameplate';

interface LocalParticipantPipProps {
  /** Hide the camera tile and render the audio placeholder instead. */
  audioOnly?: boolean;
  /** Visible while mic is muted — drives the overlay badge. */
  isMicMuted?: boolean;
  /** Human-readable name for the local participant ("You" fallback). */
  displayName?: string;
  /** Role label for the local participant ("Client", "Chief Psychologist"). */
  displayRole?: string;
}

/**
 * Floating picture-in-picture tile for the local participant. Stays
 * edge-pinned by default (top-right), but the user can drag it anywhere
 * inside the stage. Draggable via pointer events — no external lib.
 */
export function LocalParticipantPip({
  audioOnly = false,
  isMicMuted = false,
  displayName = 'You',
  displayRole = '',
}: LocalParticipantPipProps) {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }]);
  const localCamera = tracks.find((t) => t.participant.isLocal);

  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originX: offset.x,
        originY: offset.y,
      };
    },
    [offset],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    setOffset({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // Reset offset if the window resizes past the current translation.
  useEffect(() => {
    function onResize() {
      setOffset({ x: 0, y: 0 });
    }
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  if (audioOnly || !localCamera) {
    return (
      <div
        role="img"
        aria-label="Your audio is active"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ transform: `translate3d(${String(offset.x)}px, ${String(offset.y)}px, 0)` }}
        className="absolute top-6 right-6 z-20 flex h-[135px] w-[180px] cursor-grab touch-none items-center justify-center rounded-[14px] border border-white/10 bg-[#0F1F1C] shadow-[0_8px_24px_rgba(0,0,0,0.4)] active:cursor-grabbing"
      >
        <div className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-[#35816E]/20 text-[#6AA489]">
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden="true">
              <path
                d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <p className="text-[12px] text-white/70">{displayName}</p>
          {displayRole ? (
            <p className="tracking-label mt-0.5 text-[9px] text-white/40 uppercase">
              {displayRole}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{ transform: `translate3d(${String(offset.x)}px, ${String(offset.y)}px, 0)` }}
      className="absolute top-6 right-6 z-20 flex h-[135px] w-[180px] cursor-grab touch-none overflow-hidden rounded-[14px] border border-white/10 bg-[#0F1F1C] shadow-[0_8px_24px_rgba(0,0,0,0.4)] ring-1 ring-[#35816E]/40 active:cursor-grabbing"
    >
      <ParticipantTile
        trackRef={localCamera}
        disableSpeakingIndicator
        className="!h-full !w-full !rounded-none !border-0 !bg-transparent"
      />
      <ParticipantNameplate name={displayName} role={displayRole} isMuted={isMicMuted} size="sm" />
    </div>
  );
}
